<template>
  <footer class="bg-(--brand-black) text-white">
    <!-- Mobile follows home_page_mobile.pdf: logo | studio mark on the first row, legal links
         left + socials right on the second. From md up `contents` flattens the second wrapper
         so the three groups sit in one spread row as before. -->
    <div
      class="flex page-container flex-col gap-8 py-10 lg:flex-row lg:items-center lg:justify-between lg:py-16"
      :class="{ 'max-md:px-10!': isGuestHome }"
    >
      <div class="flex items-center justify-between gap-6 md:justify-start">
        <SiteLogo size="sm" class="mb-1" />
        <img
          src="/img/md_logo.png"
          alt="Interior studio MARTINA DESIGN"
          class="h-7 w-auto md:h-8"
        />
      </div>

      <div class="flex flex-row-reverse items-center justify-between gap-4 md:contents">
        <ul class="flex shrink-0 items-center gap-1.5 text-white md:gap-4">
          <li v-for="s in socials" :key="s.network">
            <a
              :href="s.href"
              target="_blank"
              rel="noopener"
              :aria-label="s.label"
              class="flex h-5 w-5 items-center justify-center rounded-full border border-white transition hover:border-white hover:text-white md:h-7 md:w-7"
            >
              <svg
                :viewBox="s.viewBox"
                class="h-3 w-3 md:h-3.5 md:w-3.5"
                fill="currentColor"
                aria-hidden="true"
              >
                <path :d="s.path" />
              </svg>
            </a>
          </li>
        </ul>

        <nav
          class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[9px] tracking-wide text-white md:text-sm"
        >
          <NuxtLinkLocale
            :to="{ name: 'terms-and-conditions' }"
            class="whitespace-nowrap hover:text-white"
          >
            {{ t('Podmínky členství') }}
          </NuxtLinkLocale>
          <span class="hidden md:inline text-white">|</span>
          <NuxtLinkLocale
            :to="{ name: 'privacy-policy' }"
            class="whitespace-nowrap hover:text-white"
          >
            {{ t('Ochrana osobních údajů') }}
          </NuxtLinkLocale>
          <span class="hidden md:inline text-white">|</span>
          <button
            type="button"
            class="cursor-pointer whitespace-nowrap hover:text-white"
            @click="showPreferences()"
          >
            {{ t('Nastavení cookies') }}
          </button>
        </nav>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const {
  $i18n: { t },
} = useNuxtApp()

// Re-opens the cookie preferences modal — required so consent stays revocable.
const { showPreferences } = useCookieConsent()

// Mobile gutter: the guest sales home (home_page_mobile.pdf) uses a wider 2.5rem gutter,
// every other route keeps `page-container`'s default 1.5rem.
const route = useRoute()
const auth = useAuth()
const isGuestHome = computed(() => route.meta.homePage === true && !auth.value?.user)

const query = useContactSettings()
// Block SSR until settings load so social links are in the HTML (SEO). Swallow errors —
// a failed settings fetch must not break the whole page, footer just renders without icons.
await query.suspense().catch(() => {})
const { data: contacts } = query

// Icon path data: Font Awesome 5 Free brand icons (CC BY 4.0).
// Order follows the backend enum: facebook, instagram, twitter, youtube, pinterest, tiktok, linkedin.
const icons = [
  {
    network: 'facebook',
    label: 'Facebook',
    viewBox: '0 0 320 512',
    path: 'm279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z',
  },
  {
    network: 'instagram',
    label: 'Instagram',
    viewBox: '0 0 448 512',
    path: 'M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9S287.7 141 224.1 141m0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7s74.7 33.5 74.7 74.7s-33.6 74.7-74.7 74.7m146.4-194.3c0 14.9-12 26.8-26.8 26.8c-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8s26.8 12 26.8 26.8m76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9c-26.2-26.2-58-34.4-93.9-36.2c-37-2.1-147.9-2.1-184.9 0c-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9c1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0c35.9-1.7 67.7-9.9 93.9-36.2c26.2-26.2 34.4-58 36.2-93.9c2.1-37 2.1-147.8 0-184.8M398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6c-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6c-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6c29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6c11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1',
  },
  {
    network: 'twitter',
    label: 'X (Twitter)',
    viewBox: '0 0 512 512',
    path: 'M389.2 48h70.6L305.6 224.2L487 464H345L233.7 318.6L106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9zm-24.8 373.8h39.1L151.1 88h-42z',
  },
  {
    network: 'youtube',
    label: 'YouTube',
    viewBox: '0 0 576 512',
    path: 'M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597c-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821c11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305m-317.51 213.508V175.185l142.739 81.205z',
  },
  {
    network: 'pinterest',
    label: 'Pinterest',
    viewBox: '0 0 384 512',
    path: 'M204 6.5C101.4 6.5 0 74.9 0 185.6C0 256 39.6 296 63.6 296c9.9 0 15.6-27.6 15.6-35.4c0-9.3-23.7-29.1-23.7-67.8c0-80.4 61.2-137.4 140.4-137.4c68.1 0 118.5 38.7 118.5 109.8c0 53.1-21.3 152.7-90.3 152.7c-24.9 0-46.2-18-46.2-43.8c0-37.8 26.4-74.4 26.4-113.4c0-66.2-93.9-54.2-93.9 25.8c0 16.8 2.1 35.4 9.6 50.7c-13.8 59.4-42 147.9-42 209.1c0 18.9 2.7 37.5 4.5 56.4c3.4 3.8 1.7 3.4 6.9 1.5c50.4-69 48.6-82.5 71.4-172.8c12.3 23.4 44.1 36 69.3 36c106.2 0 153.9-103.5 153.9-196.8C384 71.3 298.2 6.5 204 6.5',
  },
  {
    network: 'tiktok',
    label: 'TikTok',
    viewBox: '0 0 448 512',
    path: 'M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121 121 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z',
  },
  {
    network: 'linkedin',
    label: 'LinkedIn',
    viewBox: '0 0 448 512',
    path: 'M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3M447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2c-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3c94 0 111.28 61.9 111.28 142.3V448z',
  },
]

// Only networks with a URL saved in settings get an icon.
const socials = computed(() => {
  const c = contacts.value
  if (!c) return []
  return icons
    .map((icon) => ({ ...icon, href: c[icon.network as keyof typeof c] }))
    .filter((s) => s.href)
})
</script>
